import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useUserAccess } from '../../context/UserAccessContext';
import { useCrmMailQuota } from '../../hooks/useCrmMailQuota';
import CrmMailQuotaExhaustedBanner from './CrmMailQuotaExhaustedBanner';
import CrmMailPackOffersModal, {
  confirmCrmMailPackReturnFromUrl,
} from './CrmMailPackOffersModal';

/**
 * Bandeau quota e-mail CRM + modale d’achat (style HubUP).
 */
export default function CrmMailQuotaShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, activeApp } = useUserAccess();
  const { quotaExhausted, isAdmin, refetchCompanies } = useCrmMailQuota();
  const [offersOpen, setOffersOpen] = useState(false);
  const [offersSuccess, setOffersSuccess] = useState(false);
  const [packConfirmError, setPackConfirmError] = useState(null);

  const inHubupShell =
    location.pathname.startsWith('/hubup') ||
    profile?.kind === 'hubup_only' ||
    (profile?.kind === 'dual' && activeApp === 'hubup');

  const canBuyPacks = !isAdmin && user?.crm === true;

  useEffect(() => {
    if (inHubupShell) return undefined;
    const openOffers = () => setOffersOpen(true);
    window.addEventListener('crm:open-mail-pack-offers', openOffers);
    return () => window.removeEventListener('crm:open-mail-pack-offers', openOffers);
  }, [inHubupShell]);

  useEffect(() => {
    if (inHubupShell || searchParams.get('crmMailPack') !== 'return') return;
    let cancelled = false;
    (async () => {
      const result = await confirmCrmMailPackReturnFromUrl(searchParams);
      if (cancelled) return;
      navigate(location.pathname, { replace: true });
      if (result.ok) {
        setPackConfirmError(null);
        setOffersSuccess(true);
        setOffersOpen(true);
        await refetchCompanies?.();
      } else if (result.reason === 'error') {
        setPackConfirmError(result.message);
        setOffersOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    inHubupShell,
    searchParams,
    navigate,
    location.pathname,
    refetchCompanies,
  ]);

  if (inHubupShell) return null;

  return (
    <>
      {quotaExhausted ? (
        <CrmMailQuotaExhaustedBanner
          onViewOffers={
            canBuyPacks ? () => setOffersOpen(true) : undefined
          }
        />
      ) : null}
      {packConfirmError && !offersOpen ? (
        <p className="mx-4 md:mx-6 lg:mx-8 mt-2 text-sm text-error" role="alert">
          {packConfirmError}
        </p>
      ) : null}
      <CrmMailPackOffersModal
        open={offersOpen}
        onClose={() => {
          setOffersOpen(false);
          setOffersSuccess(false);
          setPackConfirmError(null);
        }}
        initialSuccess={offersSuccess}
        initialError={packConfirmError}
        onPurchaseComplete={() => {
          void refetchCompanies?.();
        }}
      />
    </>
  );
}
